<template>
  <CRow>
    <CCol sm="12">
      <CCard>
        <CCardHeader>
          <CRow>
            <CCol sm="7">
              <CIcon name="cil-settings"/>
              <h5>Materialübersicht</h5>
            </CCol>
            <CCol sm="5" class="text-right">
              <CButton color="info" size="sm" class="mr-2" @click="openImportPage">
                <CIcon name="cil-library-add"/>
                Materialimport
              </CButton>
              <CButton color="success" size="sm" @click="newEntry">
                <CIcon name="cil-library-add"/>
                Neues Katalogmaterial
              </CButton>
            </CCol>
          </CRow>
        </CCardHeader>

        <CCardBody>
          <CRow class="mb-4">
            <CCol sm="12">
              <div class="material-overview-panel">
                <h6 class="mb-2">Materialsuche</h6>
                <p class="text-muted small mb-3">
                  Katalogmaterial und bestehendes Protokollmaterial stehen direkt als Auswahl zur Verfügung.
                </p>

                <MaterialKatalogAuswahl
                  v-model="selectedCatalogMaterial"
                  form-label="Material suchen"
                  form-label-class="d-block"
                  rules=""
                  @input="addDemoMaterial"
                />

                <div class="vorgaben mt-2 px-0" v-if="demoMaterialList.length > 0">
                  <CRow class="labels ml-0 mr-0 mt-3">
                    <CCol class="d-none d-md-block col-md-2">
                      Stück*
                    </CCol>
                    <CCol class="d-none d-md-block col-md-3">
                      Art.-Nr.
                    </CCol>
                    <CCol class="d-none d-md-block col-md-7">
                      Material
                    </CCol>
                  </CRow>

                  <CRow class="material border pl-1 pr-3 py-1 mx-0 full-repeater-row mt-2" v-for="(item, index) in demoMaterialList" :key="`demo-material-${index}`">
                    <CCol class="col-10">
                      <CRow>
                        <CCol class="mt-2" sm="2">
                          <MaterialText v-model="item.stueckzahl" placeholder="Stück" form-label="Stück*" />
                        </CCol>
                        <CCol class="mt-2" sm="3">
                          <MaterialText v-model="item.artikelnr" rules="" placeholder="Artikel Nr." form-label="Art.-Nr." />
                        </CCol>
                        <CCol class="mt-2" sm="6">
                          <MaterialText v-model="item.material" rules="" placeholder="Material" form-label="Material" />
                        </CCol>
                      </CRow>
                      <CRow v-if="item.qtySuggestions && item.qtySuggestions.length > 0">
                        <CCol sm="12" class="mt-2">
                          <div class="qty-suggestions-row">
                            <CButton
                              v-for="qty in item.qtySuggestions.slice(0, 6)"
                              :key="`demo-qty-${index}-${qty}`"
                              color="secondary"
                              variant="outline"
                              size="sm"
                              class="qty-suggestion-button"
                              @click="applyDemoQuantitySuggestion(index, qty)"
                            >
                              {{ formatQuantitySuggestionLabel(qty) }}
                            </CButton>
                          </div>
                        </CCol>
                      </CRow>
                    </CCol>
                    <CCol class="col-2 mt-2 text-right m-auto center">
                      <CButton
                        color="danger"
                        variant="outline"
                        size="sm"
                        class="center m-auto center vertical-middle"
                        v-c-tooltip="{content: 'Löschen', placement:'left'}"
                        @click="removeDemoMaterial(index)"
                      >
                        <CIcon name="cil-trash"/>
                      </CButton>
                    </CCol>
                  </CRow>
                </div>
              </div>
            </CCol>
          </CRow>

          <CTabs>
            <CTab add-tab-classes="mt-1">
              <template slot="title">
                <CIcon name="cil-list"/>
                <span>Katalogmaterial</span>
              </template>
              <CDataTable
                :items="catalogItems"
                :fields="fields"
                :table-filter="{ external: true, label: 'Suchen:', placeholder: 'hier tippen...' }"
                :table-filter-value="catalogSearch"
                :column-filter="{ external: true, lazy: false }"
                :column-filter-value="catalogColumnFilter"
                :loading="catalogLoading"
                hover
                @update:table-filter-value="onCatalogTableFilterChange"
                @update:column-filter-value="onCatalogColumnFilterChange"
              >
                <template #no-items-view><NoData/></template>

                <template #preis="{item}">
                  <td>{{ item.preis ? item.preis + ' EUR' : '-' }}</td>
                </template>

                <template #actionbar="{item}">
                  <td nowrap="" width="1%">
                    <CButton
                      color="primary"
                      variant="outline"
                      square
                      size="sm"
                      v-c-tooltip="{content: 'Editieren', placement:'left'}"
                      @click="editEntry(item.materialid)"
                    >
                      <CIcon name="cil-highlighter"/>
                    </CButton>
                    <CButton
                      color="danger"
                      variant="outline"
                      size="sm"
                      class="ml-2"
                      v-c-tooltip="{content: 'Löschen', placement:'left'}"
                      @click="deleteEntry(item.materialid)"
                    >
                      <CIcon name="cil-trash"/>
                    </CButton>
                  </td>
                </template>
              </CDataTable>
              <div class="d-flex justify-content-between align-items-center mt-3 material-overview-pagination">
                <small class="text-muted">
                  {{ catalogPageInfo }}
                </small>
                <div>
                  <CButton color="secondary" size="sm" class="mr-2" @click="resetCatalogSearch">
                    Filter zurücksetzen
                  </CButton>
                  <CButton color="light" size="sm" class="mr-2" :disabled="catalogPage <= 1" @click="enqueueCatalogOverviewLoad(catalogPage - 1)">
                    Zurück
                  </CButton>
                  <CButton color="light" size="sm" :disabled="catalogPage >= catalogTotalPages" @click="enqueueCatalogOverviewLoad(catalogPage + 1)">
                    Weiter
                  </CButton>
                </div>
              </div>
            </CTab>

            <CTab add-tab-classes="mt-1">
              <template slot="title">
                <CIcon name="cil-description"/>
                <span>Protokollmaterial</span>
              </template>
              <p class="text-muted small mb-3">
                Einträge aus älteren Protokollen sind hier sichtbar und suchbar. Sie können nicht bearbeitet werden.
              </p>
              <CDataTable
                :items="historicalItems"
                :fields="historicalFields"
                :table-filter="{ external: true, label: 'Suchen:', placeholder: 'hier tippen...' }"
                :table-filter-value="historicalSearch"
                :column-filter="{ external: true, lazy: false }"
                :column-filter-value="historicalColumnFilter"
                :loading="historicalLoading"
                hover
                @update:table-filter-value="onHistoricalTableFilterChange"
                @update:column-filter-value="onHistoricalColumnFilterChange"
              >
                <template #no-items-view><NoData/></template>
              </CDataTable>
              <div class="d-flex justify-content-between align-items-center mt-3 material-overview-pagination">
                <small class="text-muted">
                  {{ historicalPageInfo }}
                </small>
                <div>
                  <CButton color="secondary" size="sm" class="mr-2" @click="resetHistoricalSearch">
                    Filter zurücksetzen
                  </CButton>
                  <CButton color="light" size="sm" class="mr-2" :disabled="historicalPage <= 1" @click="enqueueHistoricalOverviewLoad(historicalPage - 1)">
                    Zurück
                  </CButton>
                  <CButton color="light" size="sm" :disabled="historicalPage >= historicalTotalPages" @click="enqueueHistoricalOverviewLoad(historicalPage + 1)">
                    Weiter
                  </CButton>
                </div>
              </div>
            </CTab>
          </CTabs>
        </CCardBody>
      </CCard>
    </CCol>
  </CRow>
</template>

<script>
import Vue from 'vue'
import MaterialKatalogAuswahl from '@/components/MaterialKatalogAuswahl'
import MaterialText from '@/components/FormElements/Text'
import NoData from '@/components/NoData'

export default {
  name: 'MaterialIndex',
  components: {
    MaterialKatalogAuswahl,
    MaterialText,
    NoData
  },
  data () {
    return {
      catalogOverviewItems: [],
      historicalOverviewItems: [],
      catalogSearch: '',
      historicalSearch: '',
      catalogColumnFilter: {},
      historicalColumnFilter: {},
      catalogLoading: false,
      historicalLoading: false,
      catalogRequestId: 0,
      historicalRequestId: 0,
      queuedCatalogPage: null,
      queuedHistoricalPage: null,
      catalogFilterTimer: null,
      historicalFilterTimer: null,
      catalogPage: 1,
      historicalPage: 1,
      pageSize: 25,
      catalogTotal: 0,
      historicalTotal: 0,
      selectedCatalogMaterial: null,
      demoMaterialList: [],
      fields: [
        { key: 'materialid', label: 'ID' },
        { key: 'artikelnr', label: 'Artikelnr' },
        { key: 'herstellernummer', label: 'Herstellernr' },
        { key: 'material', label: 'Materialname' },
        { key: 'hersteller', label: 'Hersteller' },
        { key: 'stueck', label: 'Standardmenge' },
        { key: 'created_by_importid', label: 'Import angelegt' },
        { key: 'updated_by_importid', label: 'Import zuletzt' },
        { key: 'qtysuggestions', label: 'Stückvorschläge' },
        { key: 'recentqtysuggestions', label: 'Zuletzt verwendet' },
        { key: 'preis', label: 'Preis' },
        { key: 'actionbar', label: '', _style: 'width:1%', sorter: false, filter: false }
      ],
      historicalFields: [
        { key: 'materialid', label: 'ID' },
        { key: 'protokollid', label: 'Protokoll-ID' },
        { key: 'artikelnr', label: 'Artikelnr' },
        { key: 'herstellernummer', label: 'Herstellernr' },
        { key: 'material', label: 'Materialname' },
        { key: 'hersteller', label: 'Hersteller' },
        { key: 'stueck', label: 'Menge' },
        { key: 'qtysuggestions', label: 'Stückvorschläge' },
        { key: 'recentqtysuggestions', label: 'Zuletzt verwendet' }
      ]
    }
  },
  computed: {
    catalogItems () {
      return this.catalogOverviewItems
    },
    historicalItems () {
      return this.historicalOverviewItems
    },
    catalogTotalPages () {
      return Math.max(1, Math.ceil(this.catalogTotal / this.pageSize))
    },
    historicalTotalPages () {
      return Math.max(1, Math.ceil(this.historicalTotal / this.pageSize))
    },
    catalogPageInfo () {
      return this.pageInfoText(this.catalogPage, this.catalogTotal)
    },
    historicalPageInfo () {
      return this.pageInfoText(this.historicalPage, this.historicalTotal)
    }
  },
  mounted () {
    this.enqueueCatalogOverviewLoad(1)
    this.enqueueHistoricalOverviewLoad(1)
  },
  beforeDestroy () {
    if (this.catalogFilterTimer) {
      window.clearTimeout(this.catalogFilterTimer)
    }
    if (this.historicalFilterTimer) {
      window.clearTimeout(this.historicalFilterTimer)
    }
  },
  methods: {
    enqueueCatalogOverviewLoad (page = 1) {
      if (this.catalogLoading) {
        this.queuedCatalogPage = page
        return
      }

      this.loadCatalogOverviewPage(page)
    },
    enqueueHistoricalOverviewLoad (page = 1) {
      if (this.historicalLoading) {
        this.queuedHistoricalPage = page
        return
      }

      this.loadHistoricalOverviewPage(page)
    },
    addDemoMaterial (catalogMaterial) {
      if (!catalogMaterial || catalogMaterial.materialid === 'no-result') {
        return
      }

      this.demoMaterialList.push({
        stueckzahl: '1',
        artikelnr: catalogMaterial.artikelnr || '',
        herstellernummer: catalogMaterial.herstellernummer || '',
        material: catalogMaterial.material || '',
        hersteller: catalogMaterial.hersteller || '',
        qtySuggestions: this.mergeQuantitySuggestions(catalogMaterial)
      })
      this.selectedCatalogMaterial = null
    },
    mergeQuantitySuggestions (catalogMaterial) {
      const primarySuggestions = Array.isArray(catalogMaterial.qtysuggestions) ? catalogMaterial.qtysuggestions : []
      const recentSuggestions = Array.isArray(catalogMaterial.recentqtysuggestions) ? catalogMaterial.recentqtysuggestions : []
      const seenSuggestions = {}

      return primarySuggestions.concat(recentSuggestions).filter((value) => {
        const normalizedValue = String(value || '').trim()
        if (normalizedValue === '' || seenSuggestions[normalizedValue]) {
          return false
        }

        seenSuggestions[normalizedValue] = true
        return true
      })
    },
    applyDemoQuantitySuggestion (index, quantity) {
      if (!this.demoMaterialList[index]) {
        return
      }

      this.$set(this.demoMaterialList[index], 'stueckzahl', this.formatQuantitySuggestionLabel(quantity))
    },
    deleteEntry (materialid) {
      const options = { okText: 'Ja', cancelText: 'Nein' }
      this.$dialog
        .confirm('Das Material wirklich löschen?', options)
        .then(() => {
          Vue.axios.delete('/material/delete/' + materialid)
            .then(() => {
              this.$snotify.error('Das Material wurde gelöscht.', {
                position: 'rightTop',
                timeout: 4000
              })
              this.loadCatalogOverviewPage(this.catalogPage)
            })
        })
        .catch(() => {})
    },
    editEntry (materialid) {
      this.$router.push({ path: `/material/editieren/${materialid}` })
    },
    formatQuantitySuggestionLabel (quantity) {
      const normalizedQuantity = String(quantity || '').trim()
      return /^[-+]?\d+(?:[.,]\d+)?$/.test(normalizedQuantity) ? `${normalizedQuantity}x` : normalizedQuantity
    },
    loadCatalogOverviewPage (page = 1) {
      const requestId = ++this.catalogRequestId
      this.catalogPage = page
      this.catalogLoading = true
      this.queuedCatalogPage = null
      Vue.axios.get('/material/overview/catalog', {
        params: {
          page: this.catalogPage,
          limit: this.pageSize,
          q: this.catalogSearch,
          filters: this.buildFilterPayload(this.catalogColumnFilter)
        }
      })
        .then((response) => {
          if (requestId !== this.catalogRequestId) {
            return
          }
          this.catalogOverviewItems = this.mapOverviewItems(response.data.items || [], false)
          this.catalogTotal = Number(response.data.total || 0)
        })
        .finally(() => {
          if (requestId === this.catalogRequestId) {
            this.catalogLoading = false
            if (this.queuedCatalogPage !== null) {
              const queuedCatalogPage = this.queuedCatalogPage
              this.queuedCatalogPage = null
              this.enqueueCatalogOverviewLoad(queuedCatalogPage)
            }
          }
        })
    },
    loadHistoricalOverviewPage (page = 1) {
      const requestId = ++this.historicalRequestId
      this.historicalPage = page
      this.historicalLoading = true
      this.queuedHistoricalPage = null
      Vue.axios.get('/material/overview/historical', {
        params: {
          page: this.historicalPage,
          limit: this.pageSize,
          q: this.historicalSearch,
          filters: this.buildFilterPayload(this.historicalColumnFilter)
        }
      })
        .then((response) => {
          if (requestId !== this.historicalRequestId) {
            return
          }
          this.historicalOverviewItems = this.mapOverviewItems(response.data.items || [], true)
          this.historicalTotal = Number(response.data.total || 0)
        })
        .finally(() => {
          if (requestId === this.historicalRequestId) {
            this.historicalLoading = false
            if (this.queuedHistoricalPage !== null) {
              const queuedHistoricalPage = this.queuedHistoricalPage
              this.queuedHistoricalPage = null
              this.enqueueHistoricalOverviewLoad(queuedHistoricalPage)
            }
          }
        })
    },
    buildFilterPayload (filters) {
      const source = filters && typeof filters === 'object' ? filters : {}
      return Object.keys(source).reduce((result, key) => {
        const value = source[key]
        if (value === null || value === undefined) {
          return result
        }

        const normalizedValue = String(value).trim()
        if (normalizedValue === '') {
          return result
        }

        result[key] = normalizedValue
        return result
      }, {})
    },
    debounceCatalogReload () {
      if (this.catalogFilterTimer) {
        window.clearTimeout(this.catalogFilterTimer)
      }
      this.catalogFilterTimer = window.setTimeout(() => {
        this.enqueueCatalogOverviewLoad(1)
      }, this.getFilterDelay(this.catalogSearch, this.catalogColumnFilter))
    },
    debounceHistoricalReload () {
      if (this.historicalFilterTimer) {
        window.clearTimeout(this.historicalFilterTimer)
      }
      this.historicalFilterTimer = window.setTimeout(() => {
        this.enqueueHistoricalOverviewLoad(1)
      }, this.getFilterDelay(this.historicalSearch, this.historicalColumnFilter))
    },
    getFilterDelay (search, filters) {
      const searchText = String(search || '').trim()
      const filterValues = Object.values(filters || {})
        .map(value => String(value || '').trim())
        .filter(value => value !== '')
      const longestInputLength = [searchText.length].concat(filterValues.map(value => value.length)).reduce((maxLength, currentLength) => {
        return Math.max(maxLength, currentLength)
      }, 0)

      if (longestInputLength === 0) {
        return 0
      }

      return longestInputLength % 2 === 0 ? 180 : 1000
    },
    onCatalogTableFilterChange (value) {
      this.catalogSearch = value || ''
      this.debounceCatalogReload()
    },
    onCatalogColumnFilterChange (value) {
      this.catalogColumnFilter = Object.assign({}, value || {})
      this.debounceCatalogReload()
    },
    onHistoricalTableFilterChange (value) {
      this.historicalSearch = value || ''
      this.debounceHistoricalReload()
    },
    onHistoricalColumnFilterChange (value) {
      this.historicalColumnFilter = Object.assign({}, value || {})
      this.debounceHistoricalReload()
    },
    mapOverviewItems (items, historical) {
      return (Array.isArray(items) ? items : []).map(material => ({
        materialid: material.materialid,
        protokollid: material.protokollid || '',
        artikelnr: material.artikelnr || '',
        herstellernummer: material.herstellernummer || '',
        material: historical ? `${material.material} *` : material.material,
        stueck: material.stueck || '',
        hersteller: material.hersteller || '',
        created_by_importid: material.created_by_importid || '',
        updated_by_importid: material.updated_by_importid || '',
        qtysuggestions: Array.isArray(material.qtysuggestions) ? material.qtysuggestions.join(', ') : '',
        recentqtysuggestions: Array.isArray(material.recentqtysuggestions) ? material.recentqtysuggestions.join(', ') : '',
        preis: material.preis || ''
      }))
    },
    pageInfoText (page, total) {
      if (total === 0) {
        return 'Keine Einträge gefunden'
      }

      const start = ((page - 1) * this.pageSize) + 1
      const end = Math.min(page * this.pageSize, total)
      return `${start}-${end} von ${total} Einträgen`
    },
    newEntry () {
      this.$router.push({ path: '/material/neu' })
    },
    openImportPage () {
      this.$router.push({ path: '/material/import' })
    },
    resetCatalogSearch () {
      this.catalogSearch = ''
      this.catalogColumnFilter = {}
      this.enqueueCatalogOverviewLoad(1)
    },
    resetHistoricalSearch () {
      this.historicalSearch = ''
      this.historicalColumnFilter = {}
      this.enqueueHistoricalOverviewLoad(1)
    },
    removeDemoMaterial (index) {
      this.demoMaterialList.splice(index, 1)
    }
  }
}
</script>

<style lang="scss">
.qty-suggestions-row {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(auto-fit, minmax(120px, max-content));
  justify-content: start;
}

.qty-suggestion-button {
  border-radius: 999px;
  font-size: 0.95rem;
  min-height: 2.5rem;
  padding: 0.45rem 0.9rem;
}

@media (max-width: 767.98px) {
  .qty-suggestions-row {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .qty-suggestion-button {
    min-height: 2.75rem;
    width: 100%;
  }
}
</style>

<style lang="scss">
.material-overview-panel {
  border: 1px solid #d8dbe0;
  border-radius: 0.25rem;
  padding: 1.25rem;
  background: #fff;
}
</style>
